import { RefObject, useEffect, useRef } from "react";

interface UseAutoScrollOptions {
   speed?: number;
   idleTime?: number;
}

export function useAutoScroll(
   containerRef: RefObject<HTMLDivElement | null>,
   { speed = 1, idleTime = 1000 }: UseAutoScrollOptions = {}
) {
   const rafRef = useRef<number | null>(null);
   const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

   useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const step = () => {
         if (!container) return;

         container.scrollTop += speed;

         if (container.scrollTop >= container.scrollHeight - container.clientHeight) {
            container.scrollTop = 0;
         }

         rafRef.current = requestAnimationFrame(step);
      };

      const startAutoScroll = () => {
         if (!rafRef.current) rafRef.current = requestAnimationFrame(step);
      };

      const stopAutoScroll = () => {
         if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
         }
      };

      const handleUserActivity = () => {
         stopAutoScroll();
         if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
         inactivityTimer.current = setTimeout(() => {
            startAutoScroll();
         }, idleTime);
      };

      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("keydown", handleUserActivity);
      window.addEventListener("click", handleUserActivity);

      startAutoScroll();

      return () => {
         stopAutoScroll();
         if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
         window.removeEventListener("mousemove", handleUserActivity);
         window.removeEventListener("keydown", handleUserActivity);
         window.removeEventListener("click", handleUserActivity);
      };
   }, [containerRef, speed, idleTime]);
}
